/**
 * usePhotostudioGeneration Hook
 *
 * Manages AI image generation for the Photostudio feature.
 * Handles generation state, image selection, and batch operations.
 */

import { useCallback, useState } from 'react';
import { useAction } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type {
  IGeneratedImage,
  IPhotostudioSettings,
  IPhotostudioReferences,
  GenerationMode,
  IUsePhotostudioGenerationReturn,
} from '@/@types/photostudio';

export function usePhotostudioGeneration(): IUsePhotostudioGenerationReturn {
  const [images, setImages] = useState<IGeneratedImage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Actions
  const generateAction = useAction(api.ai.photostudioGeneration.generatePhotostudioImages);

  // Calculate selected count
  const selectedCount = images.filter(img => img.selected).length;

  // Generate images
  const generate = useCallback(
    async (params: {
      subject: string;
      environment: string;
      settings: IPhotostudioSettings;
      references: IPhotostudioReferences;
      count: 1 | 2 | 3 | 4;
      mode: GenerationMode;
      customPrompt?: string;
      stylePrompt?: string;
    }): Promise<void> => {
      setIsGenerating(true);
      setError(null);

      try {
        const result = await generateAction({
          subject: params.subject,
          environment: params.environment,
          settings: params.settings,
          references: params.references,
          count: params.count,
          mode: params.mode,
          customPrompt: params.customPrompt,
          stylePrompt: params.stylePrompt,
        });

        if (!result.success) {
          setError(result.error || 'Generation failed');
          return;
        }

        if (result.images) {
          const newImages: IGeneratedImage[] = result.images.map(
            (img: { storageId: Id<'_storage'>; url: string }) => ({
              storageId: img.storageId,
              url: img.url,
              selected: true, // Select all by default
            })
          );

          // Append to existing images
          setImages(prev => [...prev, ...newImages]);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Generation failed';
        setError(errorMessage);
      } finally {
        setIsGenerating(false);
      }
    },
    [generateAction]
  );

  // Toggle image selection
  const toggleSelect = useCallback((index: number) => {
    setImages(prev =>
      prev.map((img, i) => (i === index ? { ...img, selected: !img.selected } : img))
    );
  }, []);

  // Select all images
  const selectAll = useCallback(() => {
    setImages(prev => prev.map(img => ({ ...img, selected: true })));
  }, []);

  // Deselect all images
  const deselectAll = useCallback(() => {
    setImages(prev => prev.map(img => ({ ...img, selected: false })));
  }, []);

  // Clear all images
  const clearImages = useCallback(() => {
    setImages([]);
    setError(null);
  }, []);

  // Get selected images for saving
  const getSelectedImages = useCallback((): Array<{ storageId: Id<'_storage'>; url: string }> => {
    return images
      .filter(img => img.selected)
      .map(img => ({ storageId: img.storageId, url: img.url }));
  }, [images]);

  return {
    images,
    isGenerating,
    error,
    generate,
    toggleSelect,
    selectAll,
    deselectAll,
    clearImages,
    selectedCount,
    getSelectedImages,
  };
}
